import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Flex, Form, Layout, Select } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';

import { OutcomeMeasuresHeader } from 'components/header';
import { Subtitle, Title } from 'components/mvp-typography';
import { push } from 'connected-react-router';
import { formatISO } from 'date-fns';
import dayjs from 'dayjs';
import { cleanPagedQuestionnaireStorage } from 'features/patient/assessment/_utils/questionnaire-local-storage';
import { ImpactLocationInput } from 'features/patient/onboarding/components/form-profile/components/impact-location-input';
import { concussionDiagnosisOptions } from 'features/patient/onboarding/components/form-profile/utils/select-options';
import { injuryMechanismOptions } from 'features/patient/onboarding/components/form-profile/utils/select-options-injury';
import { assessmentSubmitInventory } from 'lib/api/firebase/apis/apis';
import { dateFormat } from 'lib/constants/date-time';
import { PatientSubcollection } from 'lib/documents';
import { Inventory } from 'lib/documents/inventory';
import { PatientInjuryKey } from 'lib/documents/patient-injury';
import { PatientProfileKey } from 'lib/documents/patient-profile';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { selectProps } from 'lib/utils/antd/select-props';
import { useErrorList } from 'lib/utils/useErrorList';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PatientRoutes } from 'routes';

/**
 * This code is required in order for dayjs to work with AntD DatePicker
 *
 * This is bad code.
 *
 * TODO: Migrate away from AntD
 */
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const QUESTIONNAIRE_INJURY_STORAGE_KEY = 'questionnaire-injury';

const questions: { component: () => JSX.Element }[] = [
  {
    component: () => (
      <Form.Item
        hasFeedback
        className="form_item"
        name={PatientInjuryKey.injuryDate}
        label="Date of Injury"
        rules={[
          {
            required: true,
            message: 'Date of injury is required'
          }
        ]}
      >
        <DatePicker
          format={dateFormat}
          maxDate={dayjs(new Date())}
          placeholder="Date of Injury"
        />
      </Form.Item>
    )
  },
  {
    component: () => (
      <Form.Item
        hasFeedback
        className="form_item"
        name={PatientProfileKey.concussionDiagnosis}
        label="Concussion Diagnosis"
        rules={[
          {
            required: true,
            message: 'An answer is required'
          }
        ]}
      >
        <Select
          options={concussionDiagnosisOptions}
          {...selectProps}
          placeholder="Concussion Diagnosis"
        />
      </Form.Item>
    )
  },
  {
    component: () => (
      <Form.Item
        hasFeedback
        className="form_item"
        name="injuryMechanism"
        label="Mechanism of Injury"
        rules={[
          {
            required: true,
            message: 'A mechanism of injury is required'
          }
        ]}
      >
        <Select
          options={injuryMechanismOptions}
          {...selectProps}
          placeholder="Mechanism of Injury"
        />
      </Form.Item>
    )
  },
  {
    component: () => (
      <Form.Item
        hasFeedback
        name={'injuryLocation'}
        label="Location of impact"
        rules={[
          {
            required: true,
            message: 'An impact location is required'
          }
        ]}
      >
        <ImpactLocationInput />
      </Form.Item>
    )
  }
];

interface InjuryFormType {
  concussionDiagnosis: boolean;
  injuryLocation: string[];
  /**
   * A DayJS object
   * https://4x.ant.design/docs/react/replace-moment#Use-date-fns
   *
   * TODO: Use date-fns
   */
  injuryDate: { toDate: () => Date };
  injuryMechanism: string;
}

export function QuestionnaireInjury() {
  const [form] = Form.useForm<InjuryFormType>();
  const mut = useSubmitInventoryMutation(
    PatientSubcollection.InventoriesInjury
  );
  const { patientId } = useParams<{ patientId: string }>();
  const dispatch = useDispatch();

  const continueUrl = getPatientLinkUrl(
    PatientRoutes.AssessmentControlFlow,
    patientId
  );
  const errorList = useErrorList();
  useQueryErrorNotifier(mut, errorList);

  async function onFinish(value: InjuryFormType) {
    await mut.mutateAsync({
      data: {
        concussionDiagnosis: value.concussionDiagnosis,
        mechanismOfInjury: value.injuryMechanism,
        locationOfInjury: value.injuryLocation,
        injuryDate: formatISO(value.injuryDate.toDate(), {
          representation: 'date'
        })
      }
    });

    dispatch(
      push(continueUrl, {
        fromQuestionnaire: true
      })
    );
  }
  return (
    <Layout style={{ backgroundColor: 'white' }}>
      <OutcomeMeasuresHeader
        label={'Exit'}
        title="Injury Questionnaire"
        onExit={() => {
          cleanPagedQuestionnaireStorage(QUESTIONNAIRE_INJURY_STORAGE_KEY);
        }}
      />
      <Flex vertical align="center">
        <Col sm={24} md={12} lg={12}>
          <Form form={form} onFinish={onFinish} labelCol={{ span: 24 }}>
            <Flex gap={16} vertical>
              <div>
                <Title>Injury Questionnaire</Title>
                <Subtitle>Help us understand your injury.</Subtitle>
              </div>
              <Col>
                {questions.map((q, i) => (
                  <q.component key={i} />
                ))}
              </Col>
              <div>
                <BaseButton htmlType="submit" loading={mut.isLoading}>
                  Submit
                </BaseButton>
              </div>
              <ErrorListDisplay errorList={errorList} />
            </Flex>
          </Form>
        </Col>
      </Flex>
    </Layout>
  );
}

export function useSubmitInventoryMutation<
  T extends Omit<Inventory, 'date' | 'id'>
>(inventoryName: string) {
  return useMutation({
    mutationFn: (inventory: T) =>
      assessmentSubmitInventory(inventoryName, inventory)
  });
}
